// frontend/src/pages/PublicFeatures.js
// /features — public feature tour, same cream/navy shell as /for-businesses.
// Deliberately describes what the product does today; no roadmap items.

import React from "react";
import { useNavigate } from "react-router-dom";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import {
  FaCalendarAlt,
  FaUserFriends,
  FaStoreAlt,
  FaCreditCard,
  FaChartLine,
  FaTags,
  FaBoxOpen,
  FaComments,
  FaCamera,
  FaMobileAlt,
  FaShieldAlt,
  FaBell,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";
import "./ForBusinesses.css";
import "./PublicPricingFeatures.css";

const SUPPORT_EMAIL = "support@mg.ogestyle.com";

const groups = [
  {
    eyebrow: "Bookings",
    title: "A calendar that fits how a salon really runs",
    items: [
      {
        icon: <FaCalendarAlt />,
        title: "Online booking, 24/7",
        desc: "Clients book themselves against real availability — by professional, by service, by location.",
      },
      {
        icon: <FaBell />,
        title: "Reminders that cut no-shows",
        desc: "Automatic email and SMS confirmations, reminders and follow-ups.",
      },
      {
        icon: <FaMobileAlt />,
        title: "Walk-ins and same-day",
        desc: "Add a walk-in at the desk and it lands on the right professional's day, not in a separate book.",
      },
      {
        icon: <FaComments />,
        title: "Waitlist",
        desc: "When a slot opens, the next client on the list hears about it first.",
      },
    ],
  },
  {
    eyebrow: "Team & locations",
    title: "Every chair, every shop, one dashboard",
    items: [
      {
        icon: <FaUserFriends />,
        title: "Professionals & staff",
        desc: "Invite professionals, set their services, hours and permissions. Each one works from their own dashboard with their own insights and reporting — not a shared shop login.",
      },
      {
        icon: <FaStoreAlt />,
        title: "Multiple locations",
        desc: "Run several shops from one account, each with its own team, calendar and reporting.",
      },
      {
        icon: <FaCalendarAlt />,
        title: "Schedules & time off",
        desc: "Per-professional availability, breaks and blocked time that booking respects instantly.",
      },
      {
        icon: <FaShieldAlt />,
        title: "Roles and access",
        desc: "Owners, shop admins, professionals, freelancers and booth renters each see only what they should.",
      },
    ],
  },
  {
    eyebrow: "Money",
    title: "Payments, splits and payouts without spreadsheets",
    items: [
      {
        icon: <FaCreditCard />,
        title: "Card payments by Stripe",
        desc: "Clients pay online or in the chair; money settles to the right account.",
      },
      {
        icon: <FaUserFriends />,
        title: "Commission & split payments",
        desc: "Set commission per professional and let the platform split each payment automatically.",
      },
      {
        icon: <FaBoxOpen />,
        title: "Product sales",
        desc: "Sell retail products online and in shop, with stock and seller attribution.",
      },
      {
        icon: <FaTags />,
        title: "Packages",
        desc: "Sell bundles of sessions in one payment and redeem them over visits — balances are tracked for you, so there is nothing to count by hand.",
      },
    ],
  },
  {
    eyebrow: "Growth",
    title: "Tools that bring clients back",
    items: [
      {
        icon: <FaChartLine />,
        title: "Reporting",
        desc: "Revenue, bookings, retention and performance by location and by professional.",
      },
      {
        icon: <FaCamera />,
        title: "Looks",
        desc: "A public gallery of real results. Clients and professionals share photos of finished work, and other clients browse the look, see who did it and book from there.",
      },
      {
        icon: <FaComments />,
        title: "Client records",
        desc: "History, notes, preferences and contact details in one place at the chair.",
      },
      {
        icon: <FaTags />,
        title: "Promotions",
        desc: "Offer discounts and campaigns without rebuilding your service menu.",
      },
      {
        icon: <FaBell />,
        title: "Reviews & reputation",
        desc: "Collect feedback after appointments and keep your shop's rating visible.",
      },
    ],
  },
];

const included = [
  "Unlimited bookings",
  "Unlimited clients",
  "Online payments by Stripe",
  "Email & SMS reminders",
  "Packages & session balances",
  "Looks gallery",
  "Client records and notes",
  "Reporting dashboard",
  "Secure hosting and backups",
  "Support from real people",
];

function PublicFeatures() {
  const navigate = useNavigate();

  return (
    <>
      <CustomerHeader />
      <main className="fb-page">
        <section className="fb-hero fb-hero--compact">
          <div className="fb-wrap fb-center">
            <p className="fb-eyebrow">Features</p>
            <h1 className="fb-display">Everything the chair needs, nothing it doesn't</h1>
            <p className="fb-sub fb-sub--wide">
              Bookings, your team, payments and the numbers behind them — built for
              salons, studios, freelancers and booth renters on one platform.
            </p>
            <div className="fb-ctas fb-ctas--center">
              <button
                type="button"
                className="fb-btn fb-btn-primary"
                onClick={() => navigate("/plans")}
              >
                See pricing
              </button>
              <button
                type="button"
                className="fb-btn fb-btn-outline"
                onClick={() => navigate("/register-business")}
              >
                Register your business
              </button>
            </div>
          </div>
        </section>

        {groups.map((group, index) => (
          <section
            key={group.eyebrow}
            className={`fb-sec${index % 2 === 1 ? " fb-sec--white" : ""}`}
          >
            <div className="fb-wrap">
              <div className="fb-sec-head">
                <p className="fb-eyebrow">{group.eyebrow}</p>
                <h2>{group.title}</h2>
              </div>
              <div className="fb-grid pf-feature-grid">
                {group.items.map((item) => (
                  <article key={item.title} className="fb-card pf-feature-card">
                    <span className="fb-ic">{item.icon}</span>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        ))}

        <section className="fb-sec">
          <div className="fb-wrap">
            <div className="fb-sec-head">
              <h2>Included in every plan</h2>
              <p>
                No feature gates between the plans — plans differ only by how many
                professionals work at a location.
              </p>
            </div>
            <ul className="pf-included">
              {included.map((item) => (
                <li key={item}>
                  <FaCheckCircle aria-hidden="true" /> {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="fb-sec fb-navy">
          <div className="fb-wrap fb-center">
            <p className="fb-eyebrow">Next step</p>
            <h2>See what it costs for your team</h2>
            <p className="fb-sub fb-sub--wide">
              One price per location, based on the professionals who work there.
            </p>
            <div className="fb-ctas fb-ctas--center">
              <button
                type="button"
                className="fb-btn fb-btn-primary"
                onClick={() => navigate("/plans")}
              >
                View plans <FaArrowRight aria-hidden="true" />
              </button>
              <a className="fb-link fb-link--light" href={`mailto:${SUPPORT_EMAIL}`}>
                Talk to us
              </a>
            </div>
          </div>
        </section>
      </main>
      <CustomerFooter />
    </>
  );
}

export default PublicFeatures;
